import { HttpHelper, HttpResponse, HttpServiceParams } from '@ppvrbh/merchant-frontend-core';
import Auth from '@aws-amplify/auth';
import { envConfig } from '../config';
import {
  ACCESS_TOKEN_HEADER,
  AUTH_LOCAL_STORAGE_KEY,
  AUTH_USER_ROLE,
  COGNITO_CHALLENGE,
  COGNITO_RESPONSE,
} from './auth-constant';
import Axios from 'axios';
import { generalCreators } from '../redux/general/general-action';
import { GENERAL_STATE_KEY } from '../redux/general/general-reducer';
import { MODAL_TYPE } from '../redux/general/general-constant';
import { AUTH_STATE_KEY } from './auth-reducer';
import { authCreators } from './auth-action';
import { store } from '../redux/store';
import {
  clearLocalStorage,
  registerLocalStorage,
  getLocalStorage,
} from '../shared/helpers/utility';
import { BUSINESS_TYPE } from '../main/main-constant';
import { AuthService } from './auth-services';
import { jwtDecode } from 'jwt-decode';
import { AuthToken, TokenPayload } from './auth-type';

class AuthHelperClass {
  tokenExpirationInterceptorId: any;
  tokenExpireTime: Date | undefined;
  maintenance: any;
  configure = () => {
    Auth.configure({
      Auth: {
        mandatorySignIn: true,
        region: envConfig.awsCognitoRegion,
        userPoolId: envConfig.awsCognitoUserPoolId,
        userPoolWebClientId: envConfig.awsCognitoUserPoolWebClientId,
      },
    });
  };

  getCurrentSession = async () => {
    try {
      const refreshToken = getLocalStorage(AUTH_LOCAL_STORAGE_KEY.REFRESH_TOKEN);

      if (!refreshToken) {
        console.error('Token not found');
        return { error: 'Token not found', success: false };
      }

      const response = await AuthService.getCurrentSession(refreshToken);
      const data = response.data?.data;

      if (!data?.idToken) {
        console.error('Something went wrong when getting new session!');
        return { error: 'Something went wrong when getting new session!', success: false };
      }

      return { data, success: true };
    } catch (e) {
      console.error(e);
      return { error: e, success: false };
    }
  };

  forgotPassword = async (params: HttpServiceParams) => {
    try {
      const { data } = params;
      await Auth.forgotPassword(data.merchantUserId);
      return { merchantUserId: data.merchantUserId, success: true };
    } catch (e) {
      return e;
    }
  };

  forgotPasswordSubmit = async (params: HttpServiceParams) => {
    try {
      const { data } = params;
      await Auth.forgotPasswordSubmit(
        data.merchantUserId,
        data.ConfirmationCode,
        data.Password
      );
      return { success: true };
    } catch (e) {
      return e;
    }
  };

  completeNewPassword = async (params: HttpServiceParams) => {
    try {
      const { data } = params;
      await Auth.completeNewPassword(data.cognitoUser, data.newPassword, {});
      return { success: true };
    } catch (e) {
      return e;
    }
  };

  toLowerCase(email: string) {
    try {
      return email.toLowerCase();
    } catch (e) {
      console.error(e);
    }
    return email;
  }

  getRole = (tokens: AuthToken): AUTH_USER_ROLE | undefined => {
    try {
      const userInfo = jwtDecode<TokenPayload>(tokens.idToken);
      return userInfo['custom:merchantRole'];
    } catch (e) {
      return;
    }
  };

  logOut = async () => {
    try {
      this.clearAuthLocalStorage();
      HttpHelper.setHeader(ACCESS_TOKEN_HEADER, '');
      AuthHelper.tokenExpireTime = undefined;
      store.dispatch(
        authCreators.setAuthState(AUTH_STATE_KEY.ID_TOKEN, undefined)
      );
      store.dispatch(
        authCreators.setAuthState(
          AUTH_STATE_KEY.STATUS,
          COGNITO_CHALLENGE.LOGOUT_SUCCESS
        )
      );
      store.dispatch(
        authCreators.setAuthState(AUTH_STATE_KEY.EMAIL, undefined)
      );
      await Auth.signOut();
      return { success: true };
    } catch (error) {
      return error;
    }
  };

  setTokenExpireTime = (token: string) => {
    const payload = jwtDecode(token);
    if (!payload.exp) return;

    this.tokenExpireTime = new Date(payload.exp * 1000);
    registerLocalStorage(
      AUTH_LOCAL_STORAGE_KEY.EXPIRE_TIME,
      this.tokenExpireTime.getTime()
    );
  };

  createTokenExpirationInterceptor = () => {
    HttpHelper.getInstance()?.interceptors.request.eject(
      this.tokenExpirationInterceptorId
    ); // NOSONAR [typescript:S905] will support optional chaining in SonarQube 8.5
    HttpHelper.getInstance()?.interceptors.response.eject(this.maintenance); // NOSONAR [typescript:S905] will support optional chaining in SonarQube 8.5
    this.tokenExpirationInterceptorId =
      HttpHelper.getInstance()?.interceptors.request.use(
        async (config: any) => {
          // Check if token is going to expire soon..
          if (
            this.tokenExpireTime &&
            this.tokenExpireTime.getTime() - new Date().getTime() < 0 &&
            config.headers[ACCESS_TOKEN_HEADER]
          ) {
            const tokens = await this.getCurrentSession();

            if (!tokens) {
              await this.logOut();
              store.dispatch(
                generalCreators.setGeneralState(GENERAL_STATE_KEY.MODAL, {
                  visible: true,
                  onOk: () => {},
                  type: MODAL_TYPE.ERROR,
                })
              );
              throw new Axios.Cancel(
                'Request rejected from unable to renew access token...'
              );
            }

            const businessTypeId = this.getBusinessTypeId();
            const isFood = businessTypeId === BUSINESS_TYPE.FOOD;
            const idToken = tokens.data?.idToken;

            if (idToken && isFood) {
              HttpHelper.setHeader(
                ACCESS_TOKEN_HEADER,
                idToken
              );
              config.headers[ACCESS_TOKEN_HEADER] = idToken;
              this.setTokenExpireTime(idToken);
            } else {
              await this.logOut();
              store.dispatch(
                generalCreators.setGeneralState(GENERAL_STATE_KEY.MODAL, {
                  visible: true,
                  onOk: () => {},
                  type: MODAL_TYPE.ERROR,
                })
              );
              throw new Axios.Cancel(
                'Request rejected from unable to renew access token...'
              );
            }
          }
          return config;
        }
      );

    this.maintenance = HttpHelper.getInstance()?.interceptors.response.use(
      async (response: any) => {
        return response;
      },
      (error: any) => {
        if (
          error.response &&
          error.response.status === 403 &&
          error.response.data.message === 'Under Maintenance'
        ) {
          store.dispatch(
            generalCreators.setGeneralState(
              GENERAL_STATE_KEY.IS_MAINTENANCE,
              true
            )
          );
        }
        return Promise.reject(error);
      }
    );
  };

  clearAuthLocalStorage = () => {
    Object.values(AUTH_LOCAL_STORAGE_KEY).forEach((key) => {
      clearLocalStorage(key);
    });
  };

  getBusinessTypeId = () => {
    return Number(getLocalStorage(AUTH_LOCAL_STORAGE_KEY.BUSINESS_TYPE_ID));
  };

  getMerchantTypeId = () => {
    return Number(getLocalStorage(AUTH_LOCAL_STORAGE_KEY.MERCHANT_TYPE_ID));
  };
}

export const AuthHelper = new AuthHelperClass();
